import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { queueCartMutation, refreshCart } from './cart-sync.js';

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #pending = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityEvent);
  }

  #handleQuantityEvent = (event) => {
    if (event.target.closest?.('cart-items-component') === this) this.#debouncedOnChange(event);
  };

  #onQuantityChange(event) {
    if (!this.isConnected) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;
    this.updateQuantity({ line, quantity, action: quantity === 0 ? 'clear' : 'change' });
  }

  onLineItemRemove(line) {
    // Keep the row until Shopify confirms removal and returns current rendered content.
    return this.updateQuantity({ line, quantity: 0, action: 'clear' });
  }

  async updateQuantity({ line, quantity, action }) {
    const row = this.refs.cartItemRows?.[line - 1];
    const key = row?.dataset.key;
    if (!key || !Number.isInteger(quantity) || quantity < 0) return;
    const marker = cartPerformance.createStartingMarker(`${action}:user-action`);
    this.#pending++;
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
    this.refs.cartTotal?.shimmer();

    try {
      await queueCartMutation(async () => {
        // A line key remains tied to the selected item when earlier lines are removed.
        const body = JSON.stringify({ id: key, quantity });
        const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
        const cart = await response.json();
        if (!response.ok || cart.errors || cart.status || !Array.isArray(cart.items)) {
          throw new Error(typeof cart.errors === 'string' ? cart.errors : 'Unable to update this item. Please try again.');
        }
      });
      await refreshCart({ force: true });
    } catch (error) {
      await refreshCart({ force: true });
      const index = this.refs.cartItemRows?.findIndex(item => item.dataset.key === key);
      const currentLine = index >= 0 ? index + 1 : line;
      const input = this.refs.quantitySelectors?.[currentLine - 1]?.querySelector('input');
      if (input) input.value = input.defaultValue;
      const message = error instanceof Error ? error.message : 'Unable to update this item. Please try again.';
      const errorText = this.refs[`cartItemError-${currentLine}`];
      const errorContainer = this.refs[`cartItemErrorContainer-${currentLine}`];
      if (errorText && errorContainer) {
        errorText.textContent = message;
        errorContainer.classList.remove('hidden');
        errorContainer.setAttribute('role', 'alert');
      } else {
        const region = document.querySelector('[data-cart-sync-status]');
        if (region) region.textContent = message;
      }
    } finally {
      this.#pending--;
      if (!this.#pending) {
        this.classList.remove('cart-items-disabled');
        this.removeAttribute('aria-busy');
        resetShimmer(this);
      }
      cartPerformance.measureFromMarker(marker);
    }
  }

  get sectionId() {
    if (!this.dataset.sectionId) throw new Error('Section id missing');
    return this.dataset.sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
